/**
 * The catalogue's two prose columns, translated.
 *
 * ## Why this file exists at all
 *
 * A product's `display_name` and `description` live in `orobas_products`, seeded in
 * Portuguese by migration `20261009010000_orobas_web_auth.sql`. That is correct: the
 * checkout button and the receipt read those columns, and a receipt is issued by the
 * payment provider in the seller's own language regardless of which language the
 * buyer arrived from.
 *
 * It is only wrong for one thing, which is the pricing card on this site. A reader who
 * picked English would be shown three Portuguese paragraphs next to three English
 * buttons. So the site carries its own translation of the two columns, keyed by the
 * same product id, and uses the catalogue row only for what the catalogue is actually
 * the authority on: the amount, the currency, and which apps the row unlocks.
 *
 * ## Why this is not the real fix
 *
 * The real fix is a migration: a `orobas_product_translations` table, or a `jsonb`
 * column on the row, read by the API which already knows the request's language. Then
 * there is one copy of each sentence rather than two that can disagree, and the
 * receipt and the page say the same words.
 *
 * This map is the smaller change and it is honest about being smaller. The two things
 * that can go wrong are both visible rather than silent: a new product added to the
 * catalogue without an entry here renders in the database's own language, and the id
 * of a renamed product stops matching and does the same. Neither can quietly show the
 * wrong price or unlock the wrong apps, because none of that is in this file.
 *
 * When the migration happens this file should be deleted rather than kept as a
 * fallback, or it becomes the second copy of every sentence it was meant to replace.
 */

import type { Product } from '../lib/api';
import type { Locale } from './locale';
import type { ProductCopyMap } from './types';

const ptBR: ProductCopyMap = {
  orobas_default: {
    name: 'Orobas Default',
    description:
      'Acesso gratuito ao OrobasHipercube, o agente de IA super poderoso, e ao Orobas Work, a IDE nativa. Em fase beta, foco em desenvolvedores.',
  },
  orobas_silver: {
    name: 'Orobas Silver',
    description:
      'Acesso completo ao OrobasHipercube, o agente de IA super poderoso, e ao Orobas Work, a IDE nativa. Em fase beta.',
  },
  orobas_gold: {
    name: 'Orobas Gold',
    description:
      'Tudo do Silver mais modelos de IA avançados, colaboração em equipe e suporte dedicado. Em fase beta.',
  },
  orobas_suite: {
    name: 'Orobas Suite',
    description:
      'OrobasHipercube, o agente de IA super poderoso, e Orobas Work, a IDE nativa, juntos. Escritos em SwiftUI, produto da M2ON.',
  },
  orobas_hipercube: {
    name: 'OrobasHipercube',
    description:
      'O agente de IA super poderoso que vive na sua barra de menus. Chat, ferramentas, Skills e 12 provedores gratuitos. Em fase beta.',
  },
  orobas_work: {
    name: 'Orobas Work',
    description:
      'A IDE nativa em SwiftUI com Liquid Glass, realce de sintaxe, git integrado e o agente numa aba. Em fase beta.',
  },
};

/*
  The names are absent on purpose below. `Orobas Suite`, `OrobasHipercube` and
  `Orobas Work` are product names: the same three strings in every language. Only the
  description is written per language.
*/
const en: ProductCopyMap = {
  orobas_default: {
    name: 'Orobas Default',
    description:
      'Free tier. Access to OrobasHipercube, the super powerful AI agent, and Orobas Work, the native IDE. In beta, focused on developers.',
  },
  orobas_silver: {
    name: 'Orobas Silver',
    description:
      'Full access to OrobasHipercube, the super powerful AI agent, and Orobas Work, the native IDE. In beta.',
  },
  orobas_gold: {
    name: 'Orobas Gold',
    description:
      'Everything in Silver plus advanced AI models, team collaboration, and dedicated support. In beta.',
  },
  orobas_suite: {
    name: 'Orobas Suite',
    description:
      'OrobasHipercube, the super powerful AI agent, and Orobas Work, the native IDE, together. Written in SwiftUI, a product of M2ON.',
  },
  orobas_hipercube: {
    name: 'OrobasHipercube',
    description:
      'The super powerful AI agent that lives in your menu bar. Chat, real tools, Skills and 12 free providers. In beta.',
  },
  orobas_work: {
    name: 'Orobas Work',
    description:
      'The native IDE in SwiftUI with Liquid Glass, syntax highlighting, built-in git, and the Agent in a tab. In beta.',
  },
};

const es: ProductCopyMap = {
  orobas_default: {
    name: 'Orobas Default',
    description:
      'Nivel gratuito. Acceso a OrobasHipercube, el agente de IA super poderoso, y Orobas Work, la IDE nativa. En beta, con foco en desarrolladores.',
  },
  orobas_silver: {
    name: 'Orobas Silver',
    description:
      'Acceso completo a OrobasHipercube, el agente de IA super poderoso, y Orobas Work, la IDE nativa. En beta.',
  },
  orobas_gold: {
    name: 'Orobas Gold',
    description:
      'Todo lo de Silver más modelos de IA avanzados, colaboración en equipo y soporte dedicado. En beta.',
  },
  orobas_suite: {
    name: 'Orobas Suite',
    description:
      'OrobasHipercube, el agente de IA super poderoso, y Orobas Work, la IDE nativa, juntos. Escritos en SwiftUI, producto de M2ON.',
  },
  orobas_hipercube: {
    name: 'OrobasHipercube',
    description:
      'El agente de IA super poderoso que vive en tu barra de menús. Chat, herramientas reales, habilidades y 12 proveedores gratuitos. En beta.',
  },
  orobas_work: {
    name: 'Orobas Work',
    description:
      'La IDE nativa en SwiftUI con cristal líquido, resaltado de sintaxis, git integrado y el Agent en una pestaña. En beta.',
  },
};

const BY_LOCALE: Readonly<Record<Locale, ProductCopyMap>> = {
  'pt-BR': ptBR,
  en,
  es,
};

/**
 * A catalogue product with its two prose columns in one language.
 *
 * The amount, the currency and the unlocked apps are returned untouched and in full,
 * because those are the columns the database owns. A product this file has never seen
 * is returned with the catalogue's own wording rather than dropped, so adding a row to
 * the catalogue does not require a deploy to sell it.
 */
export function localizeProduct(product: Product, locale: Locale): Product {
  const translated = BY_LOCALE[locale][product.id];
  if (translated === undefined) return product;

  return {
    ...product,
    name: translated.name,
    description: translated.description,
  };
}